// Editor constants & helpers

export const CATEGORIES = [
  { id: "all", label: "All" },
  { id: "flyer", label: "Flyers" },
  { id: "poster", label: "Posters" },
  { id: "social", label: "Social" },
  { id: "logo", label: "Logos" },
];

export const PRESET_SIZES = [
  { id: "flyer", label: "Flyer", sub: "1080 × 1350", width: 1080, height: 1350, category: "flyer" },
  { id: "poster", label: "Poster", sub: "1080 × 1620", width: 1080, height: 1620, category: "poster" },
  { id: "social-post", label: "Instagram Post", sub: "1080 × 1080", width: 1080, height: 1080, category: "social" },
  { id: "story", label: "Story", sub: "1080 × 1920", width: 1080, height: 1920, category: "social" },
  { id: "logo", label: "Logo", sub: "1080 × 1080", width: 1080, height: 1080, category: "logo" },
  { id: "letter", label: "Letter / Doc", sub: "1275 × 1650", width: 1275, height: 1650, category: "flyer" },
];

export const PRO_FONTS = ["NEXA", "Black Signature", "Hijrnotes", "Horsemen"];

export const FONTS = [
  "Outfit", "Manrope", "Playfair Display", "Bebas Neue", "Montserrat",
  "Oswald", "Poppins", "Anton", "Pacifico", "Lobster",
  "Dancing Script", "Archivo Black", "Caveat", "Roboto Slab",
  "Orbitron", "Bungee", "Fredoka", "Audiowide", "Monoton",
  "Press Start 2P", "VT323", "Rye", "Permanent Marker", "Staatliches",
  // Sans / modern
  "Inter", "Roboto", "Raleway", "Jost", "League Gothic",
  // Elegant serif / display
  "Cinzel", "Lora", "EB Garamond", "Cormorant Garamond", "Bodoni Moda",
  "Libre Baskerville", "Libre Caslon Display",
  // Script / signature
  "Allura", "Great Vibes", "Yellowtail", "Sacramento",
  // Custom (user-provided permanent fonts)
  "NEXA", "Black Signature", "Hijrnotes", "Horsemen",
];

export const COLOR_SWATCHES = [
  "#000000", "#1f2937", "#475569", "#94a3b8", "#ffffff",
  "#ef4444", "#f97316", "#f59e0b", "#eab308", "#84cc16",
  "#22c55e", "#10b981", "#14b8a6", "#06b6d4", "#3b82f6",
  "#6366f1", "#8b5cf6", "#a855f7", "#d946ef", "#ec4899",
  "#0b0226", "#052e16", "#7c2d12", "#fff7ed", "#fef3c7",
];

// Target sizes for the one-click "Resize for…" feature.
export const RESIZE_SIZES = [
  { id: "ig-post", label: "Instagram Post", sub: "1080 × 1080", width: 1080, height: 1080, category: "social" },
  { id: "ig-story", label: "Instagram Story", sub: "1080 × 1920", width: 1080, height: 1920, category: "social" },
  { id: "fb-post", label: "Facebook Post", sub: "1200 × 630", width: 1200, height: 630, category: "social" },
  { id: "x-post", label: "X / Twitter Post", sub: "1600 × 900", width: 1600, height: 900, category: "social" },
  { id: "flyer", label: "Flyer", sub: "1080 × 1350", width: 1080, height: 1350, category: "flyer" },
  { id: "poster", label: "Poster", sub: "1080 × 1620", width: 1080, height: 1620, category: "poster" },
  { id: "letter", label: "Letter / Doc", sub: "1275 × 1650", width: 1275, height: 1650, category: "flyer" },
  { id: "presentation", label: "Presentation", sub: "1920 × 1080", width: 1920, height: 1080, category: "social" },
];

// Proportionally scales & re-centers every element to fit a new canvas size.
const SCALE_KEYS = [
  "x", "y", "width", "height", "fontSize", "strokeWidth", "borderRadius",
  "indentLeft", "indentRight", "indentFirst", "spaceBefore", "spaceAfter",
];
export function scaleElements(elements, oldW, oldH, newW, newH) {
  const s = Math.min(newW / oldW, newH / oldH);
  const offsetX = (newW - oldW * s) / 2;
  const offsetY = (newH - oldH * s) / 2;
  return (elements || []).map((el) => {
    const n = { ...el };
    SCALE_KEYS.forEach((k) => { if (typeof n[k] === "number") n[k] = Math.round(n[k] * s); });
    n.x = Math.round(el.x * s + offsetX);
    n.y = Math.round(el.y * s + offsetY);
    return n;
  });
}

// Photoshop-style layer blend modes (CSS mix-blend-mode values).
export const BLEND_MODES = [
  { label: "Normal", value: "normal" },
  { label: "Darken", value: "darken" },
  { label: "Multiply", value: "multiply" },
  { label: "Color Burn", value: "color-burn" },
  { label: "Lighten", value: "lighten" },
  { label: "Screen", value: "screen" },
  { label: "Color Dodge", value: "color-dodge" },
  { label: "Linear Dodge (Add)", value: "plus-lighter" },
  { label: "Overlay", value: "overlay" },
  { label: "Soft Light", value: "soft-light" },
  { label: "Hard Light", value: "hard-light" },
  { label: "Difference", value: "difference" },
  { label: "Exclusion", value: "exclusion" },
  { label: "Hue", value: "hue" },
  { label: "Saturation", value: "saturation" },
  { label: "Color", value: "color" },
  { label: "Luminosity", value: "luminosity" },
];

export const uid = () => Math.random().toString(36).slice(2, 10);

// Target sizes for the animated "Download as video" feature (social-ready).
export const SOCIAL_VIDEO_SIZES = [
  {
    platform: "Instagram",
    items: [
      { id: "ig-reel", label: "Reel / Story", sub: "1080×1920", width: 1080, height: 1920 },
      { id: "ig-post", label: "Feed Post", sub: "1080×1080", width: 1080, height: 1080 },
      { id: "ig-portrait", label: "Feed Portrait", sub: "1080×1350", width: 1080, height: 1350 },
    ],
  },
  {
    platform: "Facebook",
    items: [
      { id: "fb-story", label: "Story / Reel", sub: "1080×1920", width: 1080, height: 1920 },
      { id: "fb-feed", label: "Feed Square", sub: "1080×1080", width: 1080, height: 1080 },
      { id: "fb-land", label: "Feed Landscape", sub: "1280×720", width: 1280, height: 720 },
    ],
  },
  {
    platform: "YouTube",
    items: [
      { id: "yt-short", label: "Short", sub: "1080×1920", width: 1080, height: 1920 },
      { id: "yt-video", label: "Video / Thumbnail", sub: "1920×1080", width: 1920, height: 1080 },
    ],
  },
];

export const newTextElement = (overrides = {}) => ({
  id: uid(),
  type: "text",
  x: 140, y: 200, width: 600, height: 90, rotation: 0, opacity: 1,
  text: "Your text here",
  fontSize: 64, fontFamily: "Outfit", color: "#111827",
  fontWeight: "700", fontStyle: "normal", textAlign: "center",
  lineHeight: 1.2, letterSpacing: 0, zIndex: 1,
  effect: "none", effectColor: "#000000", effectIntensity: 50, curve: 0,
  warp: "none", warpBend: 50,
  align: "center", tracking: 0, leadingAuto: true, leading: null,
  kerning: "metrics", verticalScale: 100, horizontalScale: 100,
  baselineShift: 0, fauxBold: false, fauxItalic: false, antiAlias: "smooth",
  indentLeft: 0, indentRight: 0, indentFirst: 0, spaceBefore: 0, spaceAfter: 0,
  ...overrides,
});

export const newShapeElement = (shape, overrides = {}) => ({
  id: uid(),
  type: shape, // 'rect' | 'ellipse' | 'line'
  x: 340, y: 500, width: 400, height: shape === "line" ? 8 : 400,
  rotation: 0, opacity: 1,
  fill: shape === "line" ? "#3b82f6" : "#3b82f6",
  stroke: "", strokeWidth: 0, borderRadius: shape === "rect" ? 0 : 0,
  zIndex: 1,
  ...overrides,
});

export const newImageElement = (src, overrides = {}) => ({
  id: uid(),
  type: "image",
  x: 240, y: 300, width: 600, height: 600, rotation: 0, opacity: 1,
  src, borderRadius: 0, zIndex: 1,
  imgEffect: "none", imgEffectColor: "#ffffff", imgEffectSize: 50,
  ...overrides,
});

export const newVideoElement = (src, overrides = {}) => ({
  id: uid(),
  type: "video",
  x: 220, y: 260, width: 640, height: 640, rotation: 0, opacity: 1,
  src, borderRadius: 0, objectFit: "cover", zIndex: 1,
  loop: true, autoplay: true, muted: true, volume: 1, playOnce: false,
  ...overrides,
});

export const newEmbedElement = (embedType, url, overrides = {}) => ({
  id: uid(),
  type: "embed",
  embedType, url,
  x: 200, y: 240, width: 640, height: 360, rotation: 0, opacity: 1, zIndex: 1,
  borderRadius: 8, objectFit: "cover",
  loop: false, autoplay: false, muted: true, volume: 1,
  ...overrides,
});

export const parseEmbed = (raw) => {
  const url = (raw || "").trim();
  if (!url) return null;
  let m = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/|live\/)|youtu\.be\/)([\w-]{11})/);
  if (m) return { embedType: "youtube", videoId: m[1], url };
  if (/facebook\.com\/.+\/videos\/|facebook\.com\/watch|fb\.watch\//i.test(url)) return { embedType: "facebook", url };
  if (/^https?:\/\//i.test(url)) return { embedType: "url", url };
  return null;
};

export const embedSrc = (el) => {
  if (el.embedType === "youtube") {
    const id = el.videoId || (el.url.match(/([\w-]{11})/) || [])[1] || "";
    const p = new URLSearchParams({
      autoplay: el.autoplay === false ? "0" : "1",
      mute: el.muted === false ? "0" : "1",
      loop: el.loop ? "1" : "0",
      controls: "1", playsinline: "1", rel: "0", modestbranding: "1",
    });
    if (el.loop && id) p.set("playlist", id);
    return `https://www.youtube.com/embed/${id}?${p.toString()}`;
  }
  if (el.embedType === "facebook") {
    const p = new URLSearchParams({
      href: el.url, show_text: "false",
      autoplay: el.autoplay === false ? "false" : "true",
      mute: el.muted === false ? "false" : "true",
    });
    return `https://www.facebook.com/plugins/video.php?${p.toString()}`;
  }
  return el.url;
};

// Free, license-friendly stock photos (Unsplash/Pexels)
export const STOCK_IMAGES = [
  "https://images.unsplash.com/photo-1492684223066-81342ee5ff30?w=600&q=80",
  "https://images.unsplash.com/photo-1511671782779-c97d3d27a1d4?w=600&q=80",
  "https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=600&q=80",
  "https://images.unsplash.com/photo-1556761175-5973dc0f32e7?w=600&q=80",
  "https://images.unsplash.com/photo-1531058020387-3be344556be6?w=600&q=80",
  "https://images.unsplash.com/photo-1492447166138-50c3889fccb1?w=600&q=80",
  "https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=600&q=80",
  "https://images.unsplash.com/photo-1513151233558-d860c5398176?w=600&q=80",
  "https://images.unsplash.com/photo-1504674900247-0877df9cc836?w=600&q=80",
  "https://images.unsplash.com/photo-1469474968028-56623f02e42e?w=600&q=80",
  "https://images.unsplash.com/photo-1487058792275-0ad4aaf24ca7?w=600&q=80",
  "https://images.unsplash.com/photo-1518709268805-4e9042af9f23?w=600&q=80",
];
